/**
 * Secret team chat — /(app)/games/[id]/dm/team
 *
 * One group thread for the viewer's team in a team game (docs/team-games-plan.md
 * §9 "Team chat"). Reached from the team bubble on the viewer's team header in
 * the player drawer, or a team chat push. Reuses the DM thread surface; each
 * message shows its sender's name and colour because several teammates talk
 * here. A finished game's thread stays readable but takes no new messages.
 *
 * ⚠️ KEEP THIS A ROUTE (not a Modal) — see MessageThread for the
 * keyboard-handling rationale.
 */
import React, { useCallback, useMemo } from 'react'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { PLAYER_COLORS } from '@warcarp/ui'
import { teamLabel } from '@warcarp/api-types'
import { useAuth } from '../../../../../hooks/useAuth'
import { useMarkTeamChatRead, usePostTeamMessage, useTeamChat } from '../../../../../hooks/useTeamChat'
import { MessageThread } from '../../../../../components/game/MessageThread'
import { playerColorSlot } from '../../../../../lib/playerColor'
import {
  teamChatFirstUnreadIndex,
  teamChatMemberLine,
  teamChatTitle,
  teamThreadMessages,
} from '../../../../../lib/teamChat'

export default function TeamChatScreen() {
  const { id } = useLocalSearchParams<{ id: string }>()
  const gameId = id ?? ''
  const { user } = useAuth()
  const router = useRouter()

  const { data, error } = useTeamChat(gameId)
  const postMessage = usePostTeamMessage(gameId)
  const markRead = useMarkTeamChatRead(gameId)

  const messages = useMemo(
    () => teamThreadMessages(data, (member) => PLAYER_COLORS[playerColorSlot(member)].hex),
    [data],
  )
  // The "NEW" divider stays until the composer is focused or the thread is
  // left (MessageThread's onSeen), exactly like a 1:1 thread.
  const firstUnreadIndex = teamChatFirstUnreadIndex(data, user?.id)

  const handleSeen = useCallback(() => {
    markRead.mutate()
  }, [markRead])

  const close = useCallback(() => {
    if (router.canGoBack()) router.back()
    else router.replace(`/(app)/games/${gameId}`)
  }, [router, gameId])

  const handleSend = useCallback(
    async (content: string) => {
      await postMessage.mutateAsync({ content })
    },
    [postMessage],
  )

  const readOnly = !data || !data.messagingAvailable
  return (
    <MessageThread
      title={data ? teamChatTitle(data.team) : 'Team chat · secret'}
      subtitle={data ? `Only ${teamChatMemberLine(data.members, user?.id)} can see these` : undefined}
      messages={messages}
      currentUserId={user?.id}
      onSend={handleSend}
      isSending={postMessage.isPending}
      composerDisabled={readOnly}
      onClose={close}
      firstUnreadIndex={firstUnreadIndex}
      onSeen={data ? handleSeen : undefined}
      emptyIcon="🔒"
      emptyText="No messages yet. Plan with your team in secret."
      placeholder={
        error ? 'Team chat is unavailable'
          : !data ? 'Loading team chat…'
            : !data.messagingAvailable ? 'The game is over. Team chat is read-only.'
              : `Message ${teamLabel(data.team)}…`
      }
      domIdPrefix="team"
    />
  )
}
